import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { HomeworkDocs } from "@/components/HomeworkDocs";
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, Rocket, Sparkles, CheckCircle2, Image as ImageIcon, Users, Check } from "lucide-react";

export default function HomeworkReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hw, setHw] = useState(null);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [sel, setSel] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => { api.get("/tutor/students").then((r) => setStudents(r.data)); }, []);
  useEffect(() => { api.get("/tutor/groups").then((r) => setGroups(r.data)); }, []);
  useEffect(() => { if (hw && sel === null) setSel(hw.assigned_to || []); }, [hw, sel]);

  useEffect(() => {
    let active = true;
    let timer;
    let attempts = 0;
    const load = async () => {
      const { data } = await api.get(`/homework/${id}`);
      if (!active) return;
      setHw(data);
      attempts += 1;
      if (data.status === "processing" && attempts < 60) timer = setTimeout(load, 2000);
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [id]);
  if (!hw) return <Loading />;

  if (hw.status === "processing") {
    return (
      <div className="animate-fade-up grid place-items-center py-24 text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-[#EDE9FE] border-t-[#7C3AED]" />
        <h2 className="mt-5 font-heading text-xl font-extrabold text-[#1E1B2E]">Extracting your questions…</h2>
        <p className="mt-1 max-w-md text-sm text-[#524B6B]">Ember is reading your document and turning it into an interactive exam. This can take up to a minute for larger files.</p>
      </div>
    );
  }

  const isWriting = hw.questions[0]?.type === "writing";
  const setQ = (i, patch) => setHw({ ...hw, questions: hw.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)) });
  const setOpt = (qi, oi, text) => setQ(qi, { options: hw.questions[qi].options.map((o, idx) => (idx === oi ? { ...o, text } : o)) });

  const addOption = (qi) => {
    const opts = hw.questions[qi].options;
    const key = String.fromCharCode(65 + opts.length);
    setQ(qi, { options: [...opts, { key, text: "" }] });
  };
  const removeOption = (qi, oi) => {
    const opts = hw.questions[qi].options.filter((_, idx) => idx !== oi).map((o, idx) => ({ ...o, key: String.fromCharCode(65 + idx) }));
    setQ(qi, { options: opts });
  };
  const addQuestion = () => setHw({ ...hw, questions: [...hw.questions, { number: hw.questions.length + 1, type: "mcq", text: "", options: [{ key: "A", text: "" }, { key: "B", text: "" }, { key: "C", text: "" }, { key: "D", text: "" }], correct_answer: "", explanation: "", topic: "General", subtopic: "", difficulty: "Medium" }] });
  const removeQuestion = (i) => setHw({ ...hw, questions: hw.questions.filter((_, idx) => idx !== i).map((q, idx) => ({ ...q, number: idx + 1 })) });
  const move = (i, dir) => {
    const arr = [...hw.questions];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setHw({ ...hw, questions: arr.map((q, idx) => ({ ...q, number: idx + 1 })) });
  };

  const save = async (silent) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/homework/${id}`, { title: hw.title, mode: hw.mode, time_limit: hw.time_limit, questions: hw.questions });
      setHw(data);
      if (!silent) toast.success("Changes saved");
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    } finally { setSaving(false); }
  };

  const release = async () => {
    const ok = await save(true);
    if (!ok) return;
    try {
      await api.post(`/homework/${id}/release`);
      toast.success("Homework released to students 🎉");
      navigate("/tutor/homework");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const distribute = async () => {
    try {
      const { data } = await api.post(`/homework/${id}/distribute`, { student_ids: sel || [], replace: true });
      setHw(data);
      toast.success(`Exam distributed to ${(sel || []).length} student(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const releaseResults = async () => {
    try {
      const { data } = await api.post(`/homework/${id}/release-results`);
      toast.success(data.released > 0 ? `Results released to ${data.released} student(s) 🎉` : "No completed attempts to release yet.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const genExplanation = async (qi) => {
    const q = hw.questions[qi];
    if (!q.id) { toast.error("Save first to generate an explanation."); return; }
    toast.loading("Generating explanation…", { id: "ex" });
    try {
      const { data } = await api.post(`/homework/${id}/question/${q.id}/generate-explanation`);
      setQ(qi, { explanation: data.explanation, ai_explanation: true });
      toast.success("AI explanation added (review it)", { id: "ex" });
    } catch (err) { toast.error("Could not generate", { id: "ex" }); }
  };

  const mcqCount = hw.questions.filter((q) => q.type !== "writing").length;
  const progStudents = students.filter((s) => s.program === hw.program);
  const progGroups = groups.filter((g) => g.program === hw.program);

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/tutor/homework")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to homework</button>

      <div className="ember-card mb-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <span className={`mb-2 inline-block rounded-full px-3 py-1 text-[11px] font-bold ${hw.status === "released" ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#FFFBEB] text-[#B45309]"}`}>
              {hw.status === "released" ? "● LIVE — STUDENTS CAN SEE THIS" : "READY FOR REVIEW"}
            </span>
            <Input value={hw.title} onChange={(e) => setHw({ ...hw, title: e.target.value })} data-testid="review-title"
              className="border-0 px-0 font-heading text-2xl font-extrabold text-[#1E1B2E] focus-visible:ring-0" />
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-[#524B6B]">
              <span className="font-semibold text-[#7C3AED]">{hw.program_name}</span>
              <span>{hw.section_name}</span>
              <label className="inline-flex items-center gap-1.5">Due
                <input type="date" value={hw.due_date || ""} data-testid="review-due-date"
                  onChange={async (e) => { const v = e.target.value || null; setHw({ ...hw, due_date: v }); try { await api.put(`/homework/${hw.id}/due-date`, { due_date: v }); toast.success(v ? "Due date saved." : "Due date removed."); } catch { toast.error("Could not save due date."); } }}
                  className="rounded-lg border border-[#E9E5F5] px-2 py-0.5 text-sm" />
              </label>
              <span>{mcqCount} question{mcqCount === 1 ? "" : "s"}</span>
              <span>~{hw.mode === "timed" ? `${hw.time_limit} min` : "Untimed"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-btn" className="h-10 rounded-xl"><Save className="mr-1.5 h-4 w-4" /> Save</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="release-btn" className="h-10 rounded-xl ember-gradient font-bold shadow-md shadow-violet-500/25"><Rocket className="mr-1.5 h-4 w-4" /> {hw.status === "released" ? "Save & Re-publish" : "Approve & Release"}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{hw.status === "released" ? "Save changes and re-publish?" : "Approve & release this homework?"}</AlertDialogTitle>
                  <AlertDialogDescription>Students in {hw.program_name} will {hw.status === "released" ? "see the updated version" : "be able to start it immediately"}. You can still edit it afterwards.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={release} data-testid="confirm-release" className="ember-gradient">Release now</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {!isWriting && (
      <div className="ember-card mb-5 p-6" data-testid="distribute-panel">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#EDE9FE] text-[#5B21B6]"><Users className="h-4 w-4" /></span>
            <h3 className="font-heading font-bold text-[#1E1B2E]">Distribution <span className="font-normal text-[#948EAB]">— {(hw.assigned_to || []).length} student(s) assigned</span></h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setSel(progStudents.map((s) => s.id))} className="h-9 rounded-xl text-xs">Select all</Button>
            <Button size="sm" onClick={distribute} data-testid="save-distribution" className="h-9 rounded-xl ember-gradient text-xs font-bold">Save distribution</Button>
            <Button size="sm" variant="outline" onClick={releaseResults} data-testid="release-results-btn" className="h-9 rounded-xl text-xs font-bold">Release results</Button>
          </div>
        </div>
        {progStudents.length === 0 ? (
          <p className="text-sm text-[#948EAB]">No students in {hw.program_name} yet.</p>
        ) : (
          <>
            {progGroups.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="review-group-chips">
                <span className="text-xs font-semibold text-[#948EAB]">Quick select:</span>
                {progGroups.map((g) => (
                  <button key={g.id} data-testid={`review-group-chip-${g.id}`}
                    onClick={() => setSel(g.student_ids.filter((id) => progStudents.some((s) => s.id === id)))}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD6FE] bg-[#F5F3FF] px-3 py-1 text-xs font-bold text-[#5B21B6] transition-colors hover:bg-[#EDE9FE]">
                    <Users className="h-3 w-3" /> {g.name} <span className="text-[#948EAB]">({g.student_ids.length})</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {progStudents.map((s) => {
                const on = (sel || []).includes(s.id);
                return (
                  <button key={s.id} data-testid={`dist-pick-${s.id}`} onClick={() => setSel(on ? sel.filter((x) => x !== s.id) : [...(sel || []), s.id])}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${on ? "border-[#7C3AED] bg-[#FAF9FE]" : "border-[#EEEAF8] hover:border-[#C4B5FD]"}`}>
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1E1B2E]">{s.name}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <p className="mt-3 text-xs text-[#948EAB]">Students see the exam only after you save the distribution. Results stay hidden from students until you click <b>Release results</b>.</p>
      </div>
      )}

      <HomeworkDocs hw={hw} onUpdated={(d) => setHw((p) => ({ ...p, file_url: d.file_url, solution_url: d.solution_url }))} />

      {["pdf", "mixed"].includes(hw.format) && (
        <>
          <div className="ember-card p-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#1E1B2E]">Answer key <span className="font-normal text-[#948EAB]">— used for automatic marking</span></h3>
              <span className="text-xs font-semibold text-[#7C3AED]">{hw.questions.filter((q) => q.correct_answer).length}/{hw.questions.filter((q) => q.type !== "saq").length} set</span>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {hw.questions.map((q, qi) => q.type === "saq" ? null : (
                <div key={q.id} className="flex items-center gap-2 rounded-xl border border-[#EEEAF8] px-3 py-1.5" data-testid={`ak-review-${q.number}`}>
                  <span className="w-8 shrink-0 text-sm font-bold text-[#1E1B2E]">Q{q.number}</span>
                  <div className="flex flex-wrap gap-1">
                    {q.options.map((o) => {
                      const sel = q.correct_answer === o.key;
                      return (
                        <button key={o.key} data-testid={`akr-${q.number}-${o.key}`} onClick={() => setQ(qi, { correct_answer: sel ? "" : o.key })}
                          className={`grid h-7 w-7 place-items-center rounded-lg border text-xs font-bold transition-colors ${sel ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E9E5F5] text-[#7C3AED] hover:border-[#C4B5FD]"}`}>{o.key}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#948EAB]">Tip: leave a question blank to keep it ungraded. Click Save, then Approve &amp; Release.</p>
          </div>
        </>
      )}

      {!["pdf", "mixed"].includes(hw.format) && (
      <div className="space-y-5">
        {hw.extraction_status === "failed" && (
          <div className="ember-card flex items-start gap-3 border-l-4 border-l-[#D97706] p-5" data-testid="extraction-failed-banner">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#FFFBEB] text-[#D97706]"><Sparkles className="h-4 w-4" /></span>
            <div>
              <div className="font-heading font-bold text-[#1E1B2E]">We couldn't auto-read this document</div>
              <p className="mt-0.5 text-sm text-[#524B6B]">This often happens with scanned/photo PDFs that have no text layer. You can add questions manually below, or re-upload a clearer PDF/DOCX (or a photo of each page) and we'll try again. Nothing was published to students.</p>
            </div>
          </div>
        )}
        {hw.questions.map((q, qi) => (
          <div key={qi} className="ember-card p-6" data-testid={`question-editor-${qi}`}>
            <div className="mb-4 flex items-center justify-between">
              <span className="grid h-8 min-w-8 place-items-center rounded-lg ember-gradient px-2 text-sm font-bold text-white">{q.number}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(qi, -1)} className="rounded-lg p-2 text-[#948EAB] hover:bg-[#F5F3FF]"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(qi, 1)} className="rounded-lg p-2 text-[#948EAB] hover:bg-[#F5F3FF]"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => removeQuestion(qi)} data-testid={`delete-q-${qi}`} className="rounded-lg p-2 text-[#E11D48] hover:bg-[#FFF1F2]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-4">
              <div className="sm:col-span-3">
                <Label>Question / prompt</Label>
                <Textarea value={q.text} onChange={(e) => setQ(qi, { text: e.target.value, ...(q.type === "writing" ? { prompt: e.target.value } : {}) })} data-testid={`q-text-${qi}`} className="mt-1.5 min-h-[72px] rounded-xl" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={q.type} onValueChange={(v) => setQ(qi, { type: v })}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mcq">Multiple choice</SelectItem>
                    <SelectItem value="short_answer">Short answer</SelectItem>
                    <SelectItem value="writing">Writing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {q.has_diagram && (
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#FFFBEB] px-3 py-2 text-xs font-medium text-[#B45309]">
                <ImageIcon className="h-3.5 w-3.5" /> Diagram detected: {q.diagram_description || "attach an image below"}
              </div>
            )}

            {q.type === "mcq" && (
              <div className="mt-4 space-y-2">
                <Label>Answer options — click the circle to set the correct one</Label>
                {q.options.map((o, oi) => {
                  const correct = q.correct_answer?.toLowerCase() === o.key.toLowerCase();
                  return (
                    <div key={oi} className="flex items-center gap-2">
                      <button onClick={() => setQ(qi, { correct_answer: o.key })} data-testid={`set-correct-${qi}-${o.key}`}
                        className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border-2 text-sm font-bold transition-colors ${correct ? "border-[#059669] bg-[#ECFDF5] text-[#059669]" : "border-[#E9E5F5] text-[#948EAB] hover:border-[#C4B5FD]"}`}>
                        {correct ? <CheckCircle2 className="h-4 w-4" /> : o.key}
                      </button>
                      <Input value={o.text} onChange={(e) => setOpt(qi, oi, e.target.value)} placeholder={`Option ${o.key}`} className="h-9 rounded-xl" />
                      <button onClick={() => removeOption(qi, oi)} className="rounded-lg p-2 text-[#948EAB] hover:text-[#E11D48]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  );
                })}
                <Button variant="ghost" size="sm" onClick={() => addOption(qi)} className="text-[#7C3AED]"><Plus className="mr-1 h-3.5 w-3.5" /> Add option</Button>
              </div>
            )}

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div><Label className="text-xs">Topic</Label><Input value={q.topic} onChange={(e) => setQ(qi, { topic: e.target.value })} className="mt-1 h-9 rounded-xl" /></div>
              <div><Label className="text-xs">Subtopic</Label><Input value={q.subtopic} onChange={(e) => setQ(qi, { subtopic: e.target.value })} className="mt-1 h-9 rounded-xl" /></div>
              <div>
                <Label className="text-xs">Difficulty</Label>
                <Select value={q.difficulty} onValueChange={(v) => setQ(qi, { difficulty: v })}>
                  <SelectTrigger className="mt-1 h-9 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Easy">Easy</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Hard">Hard</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {q.type !== "writing" && (
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <Label>Explanation {q.ai_explanation && <span className="ml-1 rounded bg-[#EDE9FE] px-1.5 py-0.5 text-[10px] font-bold text-[#5B21B6]">AI · review</span>}</Label>
                  <Button variant="ghost" size="sm" onClick={() => genExplanation(qi)} className="text-[#7C3AED]"><Sparkles className="mr-1 h-3.5 w-3.5" /> Generate with AI</Button>
                </div>
                <Textarea value={q.explanation} onChange={(e) => setQ(qi, { explanation: e.target.value, ai_explanation: false })} placeholder="Worked solution shown to students after they submit" className="mt-1.5 rounded-xl" />
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {!["pdf", "mixed"].includes(hw.format) && (
      <Button variant="outline" onClick={addQuestion} data-testid="add-question-btn" className="mt-5 h-11 w-full rounded-xl border-dashed border-[#DDD6FE] text-[#7C3AED]"><Plus className="mr-1.5 h-4 w-4" /> Add a question</Button>
      )}
    </div>
  );
}
